import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus, Trash2, Shield, MapPin, Megaphone } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "lga_admin", assigned_lga: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const load = () => { setLoading(true); api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/structure/lgas").then((r) => setLgas(r.data.lgas)).catch(() => {}); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/admin/users", {
        name: f.name, email: f.email, password: f.password, role: f.role,
        assigned_lga: f.role === "lga_admin" ? f.assigned_lga : undefined,
      });
      toast.success(f.role === "media_admin" ? "Media & Press admin created" : "LGA Admin created");
      setOpen(false); setF({ name: "", email: "", password: "", role: "lga_admin", assigned_lga: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/users/${id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const valid = f.name && f.email && f.password.length >= 6 && (f.role === "media_admin" || f.assigned_lga);

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading font-black text-3xl">User Management</h1>
          <p className="text-muted-foreground">Create LGA admins with access scoped to a single LGA.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-admin-btn" className="rounded-full bg-primary font-bold gap-2"><UserPlus className="w-4 h-4" /> Add LGA Admin</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Admin</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="mb-1.5 block text-sm font-semibold">Full Name</Label><Input data-testid="admin-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Coordinator name" /></div>
              <div><Label className="mb-1.5 block text-sm font-semibold">Email</Label><Input data-testid="admin-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="name@lbm.ng" /></div>
              <div><Label className="mb-1.5 block text-sm font-semibold">Password</Label><Input data-testid="admin-password" type="password" value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="Min 6 characters" /></div>
              <div><Label className="mb-1.5 block text-sm font-semibold">Role</Label>
                <Select value={f.role} onValueChange={(v) => set("role", v)}>
                  <SelectTrigger data-testid="admin-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lga_admin">LGA Admin (scoped to one LGA)</SelectItem>
                    <SelectItem value="media_admin">Media & Press Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {f.role === "lga_admin" && (
                <div><Label className="mb-1.5 block text-sm font-semibold">Assigned LGA</Label>
                  <Select value={f.assigned_lga} onValueChange={(v) => set("assigned_lga", v)}>
                    <SelectTrigger data-testid="admin-lga"><SelectValue placeholder="Select LGA" /></SelectTrigger>
                    <SelectContent>{lgas.map((l) => <SelectItem key={l.code} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="admin-create" onClick={create} disabled={!valid || saving} className="bg-primary gap-2">{saving && <Loader2 className="w-4 h-4 animate-spin" />}Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Scope</th><th className="px-4 py-3"></th></tr></thead>
            <tbody>{users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-slate-50">
                <td className="px-4 py-3 font-semibold">{u.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3">{u.role === "super_admin" ? <Badge className="bg-accent/10 text-accent hover:bg-accent/10 gap-1"><Shield className="w-3 h-3" /> Super Admin</Badge> : u.role === "media_admin" ? <Badge className="bg-primary/10 text-primary hover:bg-primary/10 gap-1"><Megaphone className="w-3 h-3" /> Media Admin</Badge> : <Badge variant="secondary" className="gap-1"><MapPin className="w-3 h-3" /> LGA Admin</Badge>}</td>
                <td className="px-4 py-3">{u.role === "media_admin" ? "Media & Press" : (u.assigned_lga || "Statewide")}</td>
                <td className="px-4 py-3 text-right">
                  {u.role !== "super_admin" && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button data-testid={`delete-user-${u.id}`} size="icon" variant="ghost" className="text-accent hover:text-accent hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Remove {u.name}?</AlertDialogTitle><AlertDialogDescription>This LGA admin will lose all access immediately.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(u.id)} className="bg-accent">Remove</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
